import ChoicePicker from '../components/ChoicePicker';
import CheckboxConsentInput from '../components/CheckboxConsentInput';
import TextInput from '../components/TextInput';
import WheelDatePicker, { type WheelDateValue } from '../components/WheelDatePicker';
import { sendIcon } from '../chatIcons';
import { stepQuestionKey } from './siteArrivalConfirmationSteps';
import type { useSiteArrivalConfirmationFlow } from './useSiteArrivalConfirmationFlow';
import type { YesNo } from './siteArrivalConfirmationTypes';

type Flow = ReturnType<typeof useSiteArrivalConfirmationFlow>;

const YES_NO = (t: Flow['t']) => [
  { value: 'yes' as const, label: t('submitDocuments.siteArrivalConfirmation.yes') },
  { value: 'no' as const, label: t('submitDocuments.siteArrivalConfirmation.no') },
];

const USE_YES_NO = (t: Flow['t']) => [
  { value: 'yes' as const, label: t('submitDocuments.siteArrivalConfirmation.useYes') },
  { value: 'no' as const, label: t('submitDocuments.siteArrivalConfirmation.useNo') },
];

const ACCOMMODATION_OPTS = (t: Flow['t']) => [
  { value: 'yes' as const, label: t('submitDocuments.siteArrivalConfirmation.accommodationYes') },
  { value: 'no' as const, label: t('submitDocuments.siteArrivalConfirmation.accommodationNo') },
];

function parseIsoDate(s: string): WheelDateValue | null {
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  return { year: Number(m[1]), month: Number(m[2]), day: Number(m[3]) };
}

export default function SiteArrivalConfirmationChatComposer({
  flow,
  wheelLocale,
}: {
  flow: Flow;
  wheelLocale: string;
}) {
  const {
    currentStep,
    t,
    values,
    advanceChoice,
    advanceDate,
    advanceText,
    advanceAgreeDorm,
    advanceAgreeMeal,
    agreeDormChecked,
    setAgreeDormChecked,
    agreeMealChecked,
    setAgreeMealChecked,
    textDraft,
    setTextDraft,
    dateDraft,
    setDateDraft,
    dateError,
  } = flow;

  if (!currentStep) return null;

  if (currentStep.kind === 'choice') {
    let options = YES_NO(t);
    if (
      currentStep.stepId === 'rentTv' ||
      currentStep.stepId === 'rentFutons' ||
      currentStep.stepId === 'rentFridge' ||
      currentStep.stepId === 'breakfast' ||
      currentStep.stepId === 'lunch' ||
      currentStep.stepId === 'dinner'
    ) {
      options = USE_YES_NO(t);
    }
    if (currentStep.stepId === 'companyAccommodation') {
      options = ACCOMMODATION_OPTS(t);
    }

    const raw = values[currentStep.fieldName];
    const current = raw === 'yes' || raw === 'no' ? raw : null;

    return (
      <div style={{ padding: '12px 16px' }}>
        <ChoicePicker
          value={current}
          options={options}
          onChange={(v: YesNo) => void advanceChoice(v)}
        />
      </div>
    );
  }

  if (currentStep.kind === 'date') {
    const parsed = parseIsoDate(dateDraft);
    const wheelValue: WheelDateValue = parsed ?? {
      year: new Date().getFullYear(),
      month: new Date().getMonth() + 1,
      day: new Date().getDate(),
    };
    const today = new Date();
    const healthCheckupMinYear = today.getFullYear();
    const healthCheckupMaxYear = today.getFullYear() + 1;
    return (
      <div className="ff-text-input">
        <div className="ff-text-input__fields">
          <WheelDatePicker
            locale={wheelLocale}
            value={wheelValue}
            minYear={healthCheckupMinYear}
            maxYear={healthCheckupMaxYear}
            onChange={(v) => {
              const iso = `${v.year}-${String(v.month).padStart(2, '0')}-${String(v.day).padStart(2, '0')}`;
              setDateDraft(iso);
            }}
          />
        </div>
        {dateError ? (
          <p className="ff-text-input__error" role="alert">
            {dateError}
          </p>
        ) : null}
        <div className="ff-text-input__footer">
          <button
            type="button"
            className="ff-text-input__send"
            onClick={() => void advanceDate()}
            aria-label={t('submitDocuments.profileName.send')}
          >
            <img src={sendIcon} alt="" aria-hidden />
          </button>
        </div>
      </div>
    );
  }

  if (currentStep.kind === 'text') {
    const required = currentStep.required;
    return (
      <TextInput
        fields={[
          {
            id: 'answer',
            label: '',
            hideLabel: true,
            ariaLabel: t(stepQuestionKey(currentStep.stepId)),
            required,
          },
        ]}
        values={{ answer: textDraft }}
        onChange={(_, v) => setTextDraft(v)}
        onSubmit={() => void advanceText()}
        submitAriaLabel={t('submitDocuments.profileName.send')}
        requiredMessage={t('submitDocuments.workerRegistryChat.required')}
      />
    );
  }

  if (currentStep.stepId === 'agreeDorm') {
    return (
      <CheckboxConsentInput
        checked={agreeDormChecked}
        label={t('submitDocuments.siteArrivalConfirmation.agreeCheckbox')}
        onChange={setAgreeDormChecked}
        continueLabel={t('submitDocuments.chat.continue')}
        onContinue={() => void advanceAgreeDorm()}
      />
    );
  }

  if (currentStep.stepId === 'agreeMeal') {
    return (
      <CheckboxConsentInput
        checked={agreeMealChecked}
        label={t('submitDocuments.siteArrivalConfirmation.agreeCheckbox')}
        onChange={setAgreeMealChecked}
        continueLabel={t('submitDocuments.chat.continue')}
        onContinue={() => void advanceAgreeMeal()}
      />
    );
  }

  return null;
}
